import { BOLD_CONTAINER_CLASS } from "../ui/bold-container";
import type { BaseComponentProps } from '../../types/puck';
import { DisplayHeading } from "../ui/display-heading";
import { ServiceName } from "./services-parts";
import { cn } from "../../lib/utils";
import { formatPriceCents } from "../../lib/price-label";

type ServicesVariant = "minimal" | "cards" | "feature" | "bold";

interface ServiceItem {
  title: string;
  description: string;
  href?: string;
  /** The price the job starts at, in cents: only on a card listed from a job type whose owner published it. */
  priceCents?: number | null;
}

interface ServicesProps extends BaseComponentProps {
  heading: string;
  subheading?: string;
  services: ServiceItem[];
  layout: "grid" | "list" | "compact" | "carousel" | "expandable";
  columns: "2" | "3" | "4";
  variant?: ServicesVariant;
  /** "yes" lists the business's job types as they are at publish in place of the saved cards. */
  useBusinessServices?: "yes" | "no";
  backgroundColor?: string;
}

// Editor-saved cards can hold a blank or missing description; either renders no paragraph.
const hasText = (value: string | undefined): boolean => (value ?? "").trim() !== "",

  /**
   * Whether a card has a price to show. A zero says nothing a customer can use.
   * @param {ServiceItem} service The card.
   * @returns {boolean} True for a published price above zero.
   */
  hasPrice = (service: ServiceItem): service is ServiceItem & { priceCents: number } =>
    typeof service.priceCents === "number" && service.priceCents > 0,

  /** Grid columns for the bold cards, by the most columns the pro chose: one on a phone, more as the screen allows. */
  BOLD_GRID_COLS: Record<ServicesProps["columns"], string> = {
    "2": "jobsite:sm:grid-cols-2",
    "3": "jobsite:sm:grid-cols-2 jobsite:lg:grid-cols-3",
    "4": "jobsite:sm:grid-cols-2 jobsite:lg:grid-cols-3 jobsite:xl:grid-cols-4",
  },

  getGridCols = (columns: ServicesProps["columns"]): string => {
    switch (columns) {
      case "2": {
        return "jobsite:md:grid-cols-2";
      }
      case "3": {
        return "jobsite:md:grid-cols-2 jobsite:xl:grid-cols-3";
      }
      case "4": {
        return "jobsite:md:grid-cols-2 jobsite:xl:grid-cols-4";
      }
      default: {
        return "jobsite:md:grid-cols-2 jobsite:xl:grid-cols-3";
      }
    }
  },

  Services: React.FC<ServicesProps> = ({
    heading,
    subheading,
    services: servicesProp,
    layout = "grid",
    columns = "3",
    variant = "cards",
    backgroundColor,
    id,
    className,
  }) => {
    const services = Array.isArray(servicesProp) ? servicesProp : [],
      surfaceRadiusStyle = { borderRadius: "var(--jobsite-radius)" } as const,

      renderServiceItem = (service: ServiceItem, idx: number) => {
        const isGrid = layout === "grid",
          cardClass = cn(
            "jobsite:group jobsite:h-full",
            variant === "minimal" && isGrid && "jobsite:text-center",
            variant === "minimal" &&
              !isGrid &&
              "jobsite:flex jobsite:gap-4 jobsite:border-b jobsite:border-border/70 jobsite:pb-5 jobsite:last:border-b-0",
            variant === "cards" &&
              "jobsite:border jobsite:border-border/70 jobsite:bg-background jobsite:p-6 jobsite:shadow-sm jobsite:transition-all jobsite:duration-200 jobsite:hover:-translate-y-0.5 jobsite:hover:border-primary/20 jobsite:hover:shadow-md",
            variant === "feature" &&
              "jobsite:border jobsite:border-border/50 jobsite:bg-linear-to-b jobsite:from-background jobsite:to-muted/30 jobsite:p-6 jobsite:shadow-sm",
            variant === "feature" && !isGrid && "jobsite:border-l-4 jobsite:border-primary jobsite:pl-5",
          ),
          titleClass = cn(
            "jobsite:font-heading jobsite:text-foreground",
            variant === "minimal"
              ? "jobsite:text-lg jobsite:font-semibold"
              : "jobsite:text-xl jobsite:font-semibold",
            variant === "cards" && "jobsite:group-hover:text-primary",
          ),
          bodyClass = cn(
            "jobsite:leading-relaxed jobsite:text-muted-foreground",
            variant === "minimal" ? "jobsite:text-sm" : "jobsite:text-base",
          );

        return (
          <div
            key={`${service.title}-${idx}`}
            className={cardClass}
            style={variant === "minimal" ? undefined : surfaceRadiusStyle}
          >
            {variant === "feature" && isGrid && (
              <p className="jobsite:mb-5 jobsite:text-xs jobsite:font-semibold jobsite:uppercase jobsite:tracking-[0.2em] jobsite:text-primary/60">
                {String(idx + 1).padStart(2, "0")}
              </p>
            )}

            <div className={cn(!isGrid && "jobsite:flex-1")}>
              <h3 className={titleClass}>
                <ServiceName className={variant === "minimal" && isGrid ? "jobsite:justify-center" : undefined} service={service} />
              </h3>
              {hasText(service.description) && (
                <p className={cn("jobsite:mt-2", bodyClass)}>{service.description}</p>
              )}
              {hasPrice(service) && (
                <p className="jobsite:mt-3 jobsite:text-sm jobsite:text-muted-foreground">
                  From{" "}
                  <span className="jobsite:font-semibold jobsite:text-foreground">
                    {formatPriceCents(service.priceCents)}
                  </span>
                </p>
              )}
            </div>
          </div>
        );
      },

      /**
       * One bold card: a square white card under an accent bar, the title in the
       * heading face, and the published price pinned to the bottom so a row of
       * cards lines up.
       * @param {ServiceItem} service - The card.
       * @param {number} idx - Its position, for the key.
       * @returns {React.JSX.Element} The card.
       */
      renderBoldServiceItem = (service: ServiceItem, idx: number): React.JSX.Element => (
        <div
          key={`${service.title}-${idx}`}
          className="jobsite:flex jobsite:h-full jobsite:flex-col jobsite:border-t-4 jobsite:border-accent jobsite:bg-card jobsite:p-6 jobsite:text-card-foreground jobsite:shadow-[0_1px_2px_rgb(0_0_0/0.06)] jobsite:sm:p-7"
          style={surfaceRadiusStyle}
        >
          <h3 className="jobsite:font-heading jobsite:text-xl jobsite:leading-tight jobsite:tracking-[-0.01em] jobsite:text-card-foreground jobsite:break-words jobsite:sm:text-[1.375rem]">
            <ServiceName service={service} />
          </h3>
          {hasText(service.description) && (
            <p className="jobsite:mt-3 jobsite:text-[0.9375rem] jobsite:leading-relaxed jobsite:text-muted-foreground jobsite:sm:text-base">
              {service.description}
            </p>
          )}
          {hasPrice(service) && (
            <p className="jobsite:mt-auto jobsite:pt-5 jobsite:text-sm jobsite:text-muted-foreground">
              From{" "}
              <span className="jobsite:font-heading jobsite:text-xl jobsite:text-card-foreground">
                {formatPriceCents(service.priceCents)}
              </span>
            </p>
          )}
        </div>
      );

    if (variant === "bold") {
      return (
        <section
          id={id}
          className={cn("jobsite:py-16 jobsite:md:py-24", hasText(backgroundColor) ? "" : "jobsite:bg-background", className)}
          style={hasText(backgroundColor) ? { backgroundColor } : undefined}
        >
          <div className={BOLD_CONTAINER_CLASS}>
            <DisplayHeading heading={heading} subheading={subheading} />
            <div
              className={cn(
                "jobsite:grid jobsite:gap-4 jobsite:lg:gap-5",
                layout === "grid" ? BOLD_GRID_COLS[columns] : "jobsite:max-w-4xl",
              )}
            >
              {services.map((service, idx) => renderBoldServiceItem(service, idx))}
            </div>
          </div>
        </section>
      );
    }

    return (
      <section
        id={id}
        className={cn(
          "jobsite:py-16 jobsite:md:py-24",
          backgroundColor
            ? ""
            : (variant === "feature"
              ? "jobsite:bg-muted/25"
              : "jobsite:bg-background"),
          className,
        )}
        style={{
          backgroundColor: backgroundColor || undefined,
        }}
      >
        <div className="jobsite:container jobsite:mx-auto jobsite:px-4">
          <div className="jobsite:mx-auto jobsite:mb-12 jobsite:max-w-3xl jobsite:text-center">
            <h2 className="jobsite:font-heading jobsite:text-3xl jobsite:tracking-tight jobsite:text-balance jobsite:text-foreground jobsite:sm:text-4xl jobsite:md:text-5xl">
              {heading}
            </h2>
            {subheading && (
              <p className="jobsite:mt-4 jobsite:text-lg jobsite:text-pretty jobsite:text-muted-foreground jobsite:md:text-xl">
                {subheading}
              </p>
            )}
          </div>

          {layout === "grid" ? (
            <div className={cn("jobsite:grid jobsite:gap-5 jobsite:md:gap-6", getGridCols(columns))}>
              {services.map((service, idx) => renderServiceItem(service, idx))}
            </div>
          ) : (
            <div className="jobsite:mx-auto jobsite:max-w-4xl jobsite:space-y-4">
              {services.map((service, idx) => renderServiceItem(service, idx))}
            </div>
          )}
        </div>
      </section>
    );
  };

export type { ServicesVariant, ServiceItem, ServicesProps };
export { Services };
